import ModalBase from '@/components/base/ModalBase';
import type { Contest, ContestParticipant } from '@/mocks/adminContests';
import { getContestParticipants, contestTypeLabels, contestTypeIcons, contestTypeColors, contestStatusLabels, contestStatusColors } from '@/mocks/adminContests';

interface ContestDetailModalProps {
  open: boolean;
  contest: Contest | null;
  mode: 'rules' | 'winners' | 'participants';
  onClose: () => void;
}

export default function ContestDetailModal({ open, contest, mode, onClose }: ContestDetailModalProps) {
  if (!open || !contest) return null;

  const typeStyle = contestTypeColors[contest.type];
  const statusStyle = contestStatusColors[contest.status];
  const participants: ContestParticipant[] = mode === 'participants' ? getContestParticipants(contest.id) : [];

  const modeConfig = {
    rules:        { icon: 'ri-file-text-line', bg: 'bg-sky-50',      iconColor: 'text-sky-600',    title: 'Reglas del concurso' },
    winners:      { icon: 'ri-trophy-line',     bg: 'bg-amber-50',    iconColor: 'text-amber-600',  title: 'Ganador' },
    participants: { icon: 'ri-group-line',      bg: 'bg-emerald-50',  iconColor: 'text-emerald-600', title: 'Participantes' },
  };

  const cfg = modeConfig[mode];

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={cfg.title}
      subtitle={contest.title}
      size="md"
      headerIcon={
        <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center`}>
          <i className={`${cfg.icon} ${cfg.iconColor} text-sm`}></i>
        </div>
      }
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          Cerrar
        </button>
      }
    >
      <div className="space-y-4">
        {/* Type + Status badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${typeStyle.bg} ${typeStyle.text} border ${typeStyle.border}`}>
            <i className={`${contestTypeIcons[contest.type]} text-[10px]`}></i>
            {contestTypeLabels[contest.type]}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}></span>
            {contestStatusLabels[contest.status]}
          </span>
        </div>

        {/* Description */}
        <p className="text-foreground-500 text-xs">{contest.description}</p>

        {/* Prize */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100">
          <div className="w-7 h-7 rounded-lg bg-amber-200 flex items-center justify-center shrink-0">
            <i className="ri-gift-line text-amber-700 text-xs"></i>
          </div>
          <div>
            <p className="text-amber-700 text-[10px] font-semibold uppercase tracking-wide">Premio</p>
            <p className="text-amber-900 text-xs font-bold">{contest.prize}</p>
          </div>
        </div>

        {mode === 'rules' && (
          <div>
            <h5 className="text-foreground-700 text-xs font-semibold mb-2">Reglas</h5>
            <div className="p-3 rounded-xl bg-background-100 border border-background-200/70">
              {contest.rules.split('. ').filter(Boolean).map((rule, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5 border-b border-background-200/40 last:border-b-0 last:pb-0 first:pt-0">
                  <span className="w-5 h-5 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-foreground-600 text-xs leading-relaxed">{rule.trim().replace(/\.$/, '')}.</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {mode === 'winners' && (
          <div>
            <h5 className="text-foreground-700 text-xs font-semibold mb-2">Ganador</h5>
            {contest.winnerName ? (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-center">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden bg-amber-200 border-2 border-amber-300">
                  <img
                    src={contest.winnerAvatar || ''}
                    alt={contest.winnerName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 text-[9px] font-bold mb-1">
                  <i className="ri-trophy-fill text-[9px]"></i>
                  GANADOR
                </div>
                <p className="text-foreground-950 font-bold text-sm">{contest.winnerName}</p>
                <p className="text-foreground-500 text-xs mt-0.5">¡Felicidades por ganar!</p>
                <p className="text-foreground-400 text-[10px] mt-2">
                  Entre {contest.participants.toLocaleString()} participantes
                </p>
              </div>
            ) : (
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-foreground-100 flex items-center justify-center">
                  <i className="ri-time-line text-xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-xs">El ganador aún no ha sido anunciado</p>
              </div>
            )}
          </div>
        )}

        {mode === 'participants' && (
          <div>
            <h5 className="text-foreground-700 text-xs font-semibold mb-2">
              Participantes ({contest.participants.toLocaleString()})
            </h5>
            {participants.length === 0 ? (
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-foreground-100 flex items-center justify-center">
                  <i className="ri-user-search-line text-xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-xs">Aún no hay participantes registrados</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {participants.map((p, idx) => (
                  <div key={p.id} className="flex items-start gap-3 p-3 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-foreground-100 shrink-0 border border-background-200">
                      <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-foreground-900 text-xs font-semibold">{p.name}</p>
                        {idx === 0 && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold">
                            <i className="ri-star-fill text-[8px]"></i> TOP
                          </span>
                        )}
                      </div>
                      <p className="text-foreground-500 text-[11px] leading-relaxed mt-0.5 line-clamp-2">{p.entry}</p>
                      <p className="text-foreground-300 text-[9px] mt-1">
                        {new Date(p.joinedAt).toLocaleDateString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stats footer */}
        <div className="flex items-center gap-4 pt-2 border-t border-background-200/70">
          <div className="text-center">
            <p className="text-foreground-950 text-sm font-bold">{contest.participants.toLocaleString()}</p>
            <p className="text-foreground-400 text-[10px]">Participantes</p>
          </div>
          <div className="text-center">
            <p className="text-foreground-950 text-sm font-bold">{contest.shares.toLocaleString()}</p>
            <p className="text-foreground-400 text-[10px]">Compartidos</p>
          </div>
          <div className="text-center">
            <p className="text-foreground-950 text-sm font-bold">
              {new Date(contest.startDate).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
            </p>
            <p className="text-foreground-400 text-[10px]">Inicio</p>
          </div>
          <div className="text-center">
            <p className="text-foreground-950 text-sm font-bold">
              {new Date(contest.endDate).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
            </p>
            <p className="text-foreground-400 text-[10px]">Cierre</p>
          </div>
        </div>
      </div>
    </ModalBase>
  );
}